import React, { useState, useEffect, useCallback } from "react";
import { Info } from "lucide-react";
import { ContactService } from "../services/contactService";
import { ClientService } from "../services/clientService";
import { useRealtimeContacts } from "../hooks/useRealtimeContacts";
import { Table, Button, Badge, SearchBar } from "./ui";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import NewContactModal from "./NewContactModal";
import ContactDetailsModal from "./ContactDetailsModal";
import SearchableSelect from "./SearchableSelect";

/**
 * ContactsTable
 *
 * Standardized contacts table using the new UI component library.
 *
 * Features:
 * - Server-side pagination (20/50/All)
 * - Server-side search with debouncing
 * - Column sorting (all columns)
 * - Column resizing with auto-size
 * - Real-time updates
 * - Role tags with colors
 * - Phone/email formatting
 * - Row click to view details
 * - New contact modal
 */
const ContactsTable = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [showContactDetailsModal, setShowContactDetailsModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20); // Default to 20
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true); // Auto-resize enabled by default
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState(null); // null = all clients

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initial load
  useEffect(() => {
    window.scrollTo(0, 0);
    loadContacts();
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load clients for filter dropdown
  const loadClients = async () => {
    try {
      const clientsData = await ClientService.getAllClients();
      // Sort by name
      clientsData.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(clientsData);
    } catch (err) {
      console.error("Error loading clients:", err);
    }
  };

  // Real-time contact handlers
  const handleContactUpdate = useCallback((updatedContact) => {
    setContacts((prev) =>
      prev.map((contact) =>
        contact.id === updatedContact.id
          ? {
              ...updatedContact,
              billing: updatedContact.billing_yn || false,
              notes: updatedContact.notes || "",
            }
          : contact,
      ),
    );
  }, []);

  const handleContactInsert = useCallback((newContact) => {
    setContacts((prev) => [
      {
        ...newContact,
        billing: newContact.billing_yn || false,
        notes: newContact.notes || "",
      },
      ...prev,
    ]);
  }, []);

  const handleContactDelete = useCallback((deletedContactId) => {
    setContacts((prev) =>
      prev.filter((contact) => contact.id !== deletedContactId),
    );
  }, []);

  // Set up real-time subscription
  useRealtimeContacts(
    handleContactUpdate,
    handleContactInsert,
    handleContactDelete,
    true,
  );

  // Load contacts from server
  const loadContacts = async (
    page = currentPage,
    search = debouncedSearchQuery,
    size = pageSize,
    client = clientFilter,
  ) => {
    try {
      setLoading(true);
      setError(null);
      const result = await ContactService.getPaginatedContacts(
        page,
        size,
        sortField,
        sortDirection,
        search,
        client,
      );

      setContacts(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update when sort, search, or client filter changes
  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
      loadContacts(1, debouncedSearchQuery, pageSize, clientFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, debouncedSearchQuery, clientFilter]);

  // Format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return "—";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role) => {
    if (!role) return "neutral";

    const normalizedRole = role.toLowerCase();

    if (normalizedRole.includes("admin")) return "danger";
    if (normalizedRole.includes("accounts payable")) return "warning";
    if (
      normalizedRole.includes("owner") ||
      normalizedRole.includes("president")
    )
      return "success";
    if (normalizedRole.includes("project manager")) return "info";
    if (normalizedRole.includes("site supervisor")) return "warning";

    return "neutral";
  };

  // Handle row click
  const handleRowClick = (contact) => {
    setSelectedContact(contact);
    setShowContactDetailsModal(true);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadContacts(newPage, debouncedSearchQuery, pageSize, clientFilter);
      // Note: Auto-resize will happen automatically when data changes
      // via the Table component's internal useEffect
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    loadContacts(1, debouncedSearchQuery, newSize, clientFilter);
  };

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle sort events from Table component
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    // loadContacts will be triggered by useEffect when sortField/sortDirection change
  };

  // Column definitions for new Table component
  const columns = [
    {
      id: "info",
      label: "",
      field: "id",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      searchable: false,
      resizable: false,
      align: "center",
      contentType: "icon",
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          style={{
            padding: "8px",
            background: "none",
            border: "none",
            color: "var(--gray-400)",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "background-color 0.2s, color 0.2s, transform 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
            minWidth: "36px",
            minHeight: "36px",
            maxWidth: "36px",
            maxHeight: "36px",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--gray-800)";
            e.currentTarget.style.color = "var(--cta-blue)";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "var(--gray-400)";
            e.currentTarget.style.transform = "scale(1)";
          }}
          title="View contact details"
          aria-label={`View details for ${row.name}`}
        >
          <Info size={20} />
        </button>
      ),
    },
    {
      id: "name",
      label: "Name",
      field: "name",
      width: 200,
      minWidth: 100,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: "text", // Plain text content
      render: (value) => value || "—",
    },
    {
      id: "clientName",
      label: "Client",
      field: "clientName",
      width: 200,
      minWidth: 100,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: "text",
      render: (value) => value || "—",
    },
    {
      id: "phoneNumber",
      label: "Phone Number",
      field: "phoneNumber",
      width: 150,
      minWidth: 120,
      maxWidth: 250,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: "link", // Link content (styled text, no extra padding)
      formatValue: (value) => formatPhoneNumber(value), // Format for auto-resize measurement
      render: (value) =>
        value ? (
          <a
            href={`tel:${value}`}
            style={{ color: "var(--cta-blue)", textDecoration: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            {formatPhoneNumber(value)}
          </a>
        ) : (
          "—"
        ),
    },
    {
      id: "email",
      label: "Email",
      field: "email",
      width: 250,
      minWidth: 150,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: "link", // Link content (styled text, no extra padding)
      render: (value) =>
        value ? (
          <a
            href={`mailto:${value}`}
            style={{ color: "var(--cta-blue)", textDecoration: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        ) : (
          "—"
        ),
    },
    {
      id: "role",
      label: "Role",
      field: "role",
      width: 180,
      minWidth: 160, // Increased to accommodate badges like "PROJECT MANAGEMENT"
      maxWidth: 300,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: "badge", // Badge component (needs extra padding for internal padding/border)
      render: (value) =>
        value ? (
          <Badge variant={getRoleBadgeVariant(value)} size="sm">
            {value}
          </Badge>
        ) : (
          "—"
        ),
    },
    {
      id: "billing",
      label: "Billing",
      field: "billing",
      width: 100,
      minWidth: 80,
      maxWidth: 150,
      sortable: true,
      resizable: true,
      align: "center",
      contentType: "icon", // Icon/checkmark content (centered, fixed size)
      render: (value) => (
        <span style={{ fontSize: "16px" }}>{value ? "✓" : "—"}</span>
      ),
    },
    {
      id: "notes",
      label: "Notes",
      field: "notes",
      width: 300,
      minWidth: 150,
      maxWidth: 500,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: "text", // Plain text with ellipsis
      render: (value) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "—"}
        </div>
      ),
    },
  ];

  if (loading && contacts.length === 0) {
    return <LoadingSpinner message="Loading contacts..." />;
  }

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "var(--dark-bg)",
        minHeight: "100vh",
      }}
    >
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: "var(--space-4)" }}>
          <ErrorMessage
            message={error}
            onRetry={loadContacts}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--space-6)",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "var(--text-3xl)",
              fontWeight: "var(--font-bold)",
              color: "var(--gray-50)",
            }}
          >
            Contacts
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: "var(--space-2)",
              fontSize: "var(--text-sm)",
              color: "var(--gray-400)",
            }}
          >
            {totalCount} total contacts
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => setShowNewContactModal(true)}
        >
          + New Contact
        </Button>
      </div>

      {/* Table with integrated toolbar */}
      <div
        style={{
          border: "1px solid var(--gray-600)",
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        }}
      >
        {/* Search Bar, Filter, and Auto-resize Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--space-4)",
            backgroundColor: "var(--dark-bg)",
            borderBottom: "1px solid var(--gray-600)",
            gap: "var(--space-4)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              flex: 1,
            }}
          >
            <div style={{ width: "300px", flexShrink: 0 }}>
              <SearchBar
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts, companies..."
                size="sm"
              />
            </div>
            <div style={{ minWidth: "220px", maxWidth: "300px" }}>
              <SearchableSelect
                value={clientFilter || ""}
                options={clients}
                onChange={(e) => {
                  const value = e.target.value;
                  setClientFilter(value === "" ? null : parseInt(value, 10));
                }}
                placeholder="All Companies"
                getOptionLabel={(client) => client.name}
                getOptionValue={(client) => client.id}
                noOptionsText="No companies found"
              />
            </div>
          </div>
          <Button
            variant={autoResizeEnabled ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
          >
            {autoResizeEnabled ? "✓ " : ""}Auto-resize
          </Button>
        </div>

        {/* Scrollable Table Container */}
        <div
          style={{
            backgroundColor: "var(--dark-surface)",
            overflowX: "auto",
            overflowY: "auto",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Table
            data={contacts}
            columns={columns}
            sortable={true}
            sortField={sortField} // Current sort field
            sortDirection={sortDirection} // Current sort direction
            onSort={handleSort} // Handle sort events
            searchable={false} // Using external server-side search above
            resizable={true}
            paginated={false} // We're handling pagination manually with server-side
            autoResizeOnDataChange={autoResizeEnabled} // Auto-resize based on toggle state
            showAutoSizeButton={false} // Hide auto-size button, we have toggle in header
            storageKey="contacts-table" // Unique key for localStorage
            loading={loading}
            emptyMessage={
              searchQuery || clientFilter
                ? "No contacts found matching your search or filter"
                : 'No contacts found. Click "New Contact" to add your first contact.'
            }
            onRowClick={handleRowClick}
            onManualResize={handleManualResize} // Auto-disable auto-resize when user manually resizes
            variant="dense"
            stickyHeader={true}
            style={{ border: "none", borderRadius: "0", overflow: "visible" }}
          />
        </div>
        {/* End Scrollable Table Container */}

        {/* Frozen Pagination */}
        {totalCount > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--space-4)",
              backgroundColor: "var(--dark-bg)",
              borderTop: "1px solid var(--gray-600)",
              gap: "var(--space-4)",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-3)",
              }}
            >
              <div
                style={{ fontSize: "var(--text-sm)", color: "var(--gray-400)" }}
              >
                Page {currentPage} of {totalPages} ({totalCount} total contacts)
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                }}
              >
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-400)",
                  }}
                >
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>,
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Contact Modal */}
      {showNewContactModal && (
        <NewContactModal
          isOpen={showNewContactModal}
          onClose={() => setShowNewContactModal(false)}
          onContactCreated={() => {
            loadContacts();
            setShowNewContactModal(false);
          }}
        />
      )}

      {/* Contact Details Modal */}
      {showContactDetailsModal && (
        <ContactDetailsModal
          contact={selectedContact}
          isOpen={showContactDetailsModal}
          onClose={() => {
            setShowContactDetailsModal(false);
            setSelectedContact(null);
          }}
          initialEditMode={false}
          onContactUpdated={() => {
            loadContacts();
            setShowContactDetailsModal(false);
            setSelectedContact(null);
          }}
        />
      )}
    </div>
  );
};

export default ContactsTable;
